// src/contexts/GeoLocationContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Location {
  lat: number;
  lon: number;
  state?: string;
  zip?: string;
  country?: string;
}

interface GeoLocationContextType {
  location: Location | null;
  setLocation: (location: Location | null) => void;
}

const GeoLocationContext = createContext<GeoLocationContextType | undefined>(undefined);

export const useGeoLocation = () => {
  const context = useContext(GeoLocationContext);
  if (!context) {
    throw new Error('useGeoLocation must be used within a GeoLocationProvider');
  }
  return context;
};

interface GeoLocationProviderProps {
  children: ReactNode;
}

export const GeoLocationProvider: React.FC<GeoLocationProviderProps> = ({ children }) => {
  const [location, setLocation] = useState<Location | null>(() => {
    const stored = localStorage.getItem('userLocation');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Migration: default country to "united-states" for existing users
      return { ...parsed, country: parsed.country || 'united-states' };
    }
    return null; // Default to null if no stored location
  });

  // Safely access localStorage
  const getLocalStorage = (key: string) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  };

  // Safely set localStorage
  const setLocalStorage = (key: string, value: any) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {}
  };

  // Load saved location on mount only (no automatic geolocation attempt)
  useEffect(() => {
    const savedLocation = getLocalStorage('userLocation');
    if (savedLocation) {
      // Migration: default country to "united-states" for existing users
      setLocation({ ...savedLocation, country: savedLocation.country || 'united-states' });
    }
  }, []);

  // Save location to localStorage when it changes
  useEffect(() => {
    if (location) {
      setLocalStorage('userLocation', location);
    } else {
      localStorage.removeItem('userLocation'); // Clear localStorage when location is null
    }
  }, [location]);

  return <GeoLocationContext.Provider value={{ location, setLocation }}>{children}</GeoLocationContext.Provider>;
};
